import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import filesStore from 'modules/documents/components/Documents/store'
import { useCreateDocumentTemplate } from 'modules/documents/hooks/useCreateDocumentTemplate'

const deleteAllFiles = () => {
  const length = filesStore.files.length
  filesStore.files.splice(0, length)
}

const onDeleteFile = (i: number) => {
  filesStore.files.splice(i, 1)
}

const addFiles = (files: File[]) => {
  filesStore.concat(files)
}

interface Props {
  onUploadFinished: () => void
}

interface ReturnType {
  onSubmit: () => void
  submitting: boolean
  addFiles: (files: File[]) => void
  onDeleteFile: (index: number) => void
  values: FormValues
  setValues: (values: FormValues) => void
  errors: FormErrors
  deleteAllFiles: () => void
  loading: boolean
}

interface FormValues {
  companyId: number
  file: File | undefined
  name: string
}

interface FormErrors {
  companyId?: string
  file?: string
  name?: string
}

const useUpload = ({ onUploadFinished }: Props): ReturnType => {
  const { company } = useCurrentSession()
  const { createDocumentTemplate, loading } = useCreateDocumentTemplate()

  const { handleSubmit, resetForm, values, setValues, errors } = useForm<FormValues>({
    defaultValues: {
      companyId: company.id,
      file: filesStore.files[0],
      name: '',
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createDocumentTemplate({
        companyId: company.id,
        file: filesStore.files[0],
        name: data.name,
      })

      const handled = handleResult(result, (formErrors) => {
        setErrors({
          ...formErrors,
          file: formErrors.file || formErrors.errors,
        })
      })

      if (handled.success) {
        resetForm()

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.documents_uploaded'),
        })

        await onUploadFinished()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.documents_upload_failed'),
        })
      }

      return handled
    },
  })

  return {
    onSubmit: handleSubmit,
    submitting: loading,
    values,
    setValues,
    addFiles,
    onDeleteFile,
    deleteAllFiles,
    loading,
    errors,
  }
}

export default useUpload
